class SwatchGrid extends HTMLElement {
  constructor() {
    super();
    this.miniCart = document.querySelector('mini-cart');
    this.swatchCount = document.querySelector('.swatch-product-count h2');
    this.facetsWrapper = document.querySelector('.facets-wrapper');

    document.addEventListener('filter-applied', () => {
      this.updateProductCount();
      this.updateFacetsWrapperHeight();
    });
    this.updateProductCount();
    this.updateFacetsWrapperHeight();

    // Add mobile image toggle functionality
    if (window.isMobile()) {
      this.setupMobileImageToggles();
    }
  }

  setupMobileImageToggles() {
    this.addEventListener('click', (e) => {
      const imageWrapper = e.target.closest('.swatch-group__item-image-wrapper');
      if (!imageWrapper) return;

      // Prevent this click from triggering the swatch button
      e.preventDefault();
      e.stopPropagation();

      // Remove active class from all other wrappers
      this.querySelectorAll('.swatch-group__item-image-wrapper.show-hover').forEach((wrapper) => {
        if (wrapper !== imageWrapper) {
          wrapper.classList.remove('show-hover');
        }
      });

      // Toggle the hover class on the clicked wrapper
      imageWrapper.classList.toggle('show-hover');
    });
  }

  updateFacetsWrapperHeight() {
    if (this.facetsWrapper) {
      const facetsHeight = this.facetsWrapper.offsetHeight;
      this.style.setProperty('--facets-height', `${facetsHeight}px`);
    }
  }

  updateProductCount() {
    const totalProducts = this.querySelectorAll('.swatch-group__item')?.length;
    this.swatchCount.textContent = `${totalProducts} Swatches`;
  }

  /**
   * data should be an array where each item has a variant_id, title, image, and price
   */
  addToMiniCart(data) {
    this.miniCart.add(data);
  }

  removeFromMiniCart(ids) {
    if (typeof ids === 'string') {
      ids = [ids];
    }

    this.miniCart.remove(ids);
  }
}

customElements.define('swatch-grid', SwatchGrid);

class MiniCart extends HTMLElement {
  _mobile_message_threshold = 5_000;
  static STORAGE_KEY = 'miniCartProducts';

  constructor() {
    super();
    this.products = JSON.parse(sessionStorage.getItem(MiniCart.STORAGE_KEY)) || [];
    this.productsGrid = this.querySelector('.mini-cart__products');
    this.mobileAccordionMessage = this.dataset.mobileAccordionMessage;
    this.defaultMessage = this.mobileAccordionMessage;
    this.productCount = this.products.length;
    this.messageTimeout = null;
    this.addToCartButton = this.querySelector('button[name="add-to-cart"]');
    this.addToCartButton.addEventListener('click', () => this.addToCart());

    this.desktopToggleButton = this.querySelector('.mini-cart__desktop-toggle');
    this.desktopToggleButton.addEventListener('click', () => this.handleDesktopToggleClick());

    this.updateMobileMessage();
    this.render();
    this.closest('swatch-grid').classList.add('swatch-grid--mini-cart-init');
  }

  handleDesktopToggleClick() {
    if (this.desktopToggleButton.getAttribute('aria-expanded') === 'true') {
      this.productsGrid.classList.remove('mini-cart__products--expanded');
      // Remove the "Less" button if it exists
      const lessButton = this.productsGrid.querySelector('.mini-cart__less-button');
      if (lessButton) {
        lessButton.remove();
      }
      this.updateDesktopToggleCartCount();
      this.desktopToggleButton.setAttribute('aria-expanded', 'false');
    } else {
      this.productsGrid.classList.add('mini-cart__products--expanded');
      // Create and add the "Less" button using data-close-message
      const lessButton = document.createElement('button');
      lessButton.className = 'btn--underline mini-cart__desktop-toggle mini-cart__less-button';
      lessButton.textContent = this.desktopToggleButton.dataset.closeMessage;
      this.productsGrid.appendChild(lessButton);

      // Add click handler to the Less button
      lessButton.addEventListener('click', () => {
        this.productsGrid.classList.remove('mini-cart__products--expanded');
        lessButton.remove();
        this.updateDesktopToggleCartCount();
        this.desktopToggleButton.setAttribute('aria-expanded', 'false');
      });

      this.desktopToggleButton.textContent = this.desktopToggleButton.dataset.closeMessage;
      this.desktopToggleButton.setAttribute('aria-expanded', 'true');
    }
  }

  updateDesktopToggleCartCount() {
    const MOBILE_CART_LIMIT = 5;
    const DESKTOP_CART_LIMIT = 10;
    const IS_MOBILE = window.isMobile();

    this.desktopToggleButton.textContent = this.desktopToggleButton.dataset.openTemplate.replace(
      '{count}',
      this.productCount - (IS_MOBILE ? MOBILE_CART_LIMIT : DESKTOP_CART_LIMIT)
    );
  }

  add(products) {
    products.forEach((product) => {
      this.products.push(product);
    });

    this.productCount += products.length;
    this.updateMobileMessage();
    this.updateSessionStorage();

    this.dispatchEvent(
      new CustomEvent('mini-cart:add', {
        detail: { products },
      })
    );
    this.render();
  }

  remove(variantIds) {
    if (typeof variantIds === 'string') {
      variantIds = [variantIds];
    }

    const activeSwatchTiles = variantIds.map((id) => {
      return document.querySelector(`.swatch-group__item[data-swatch-id="${id}"]`);
    });
    const removedCount = this.products.filter((product) => variantIds.includes(product.variant_id)).length;
    this.products = this.products.filter((product) => !variantIds.includes(product.variant_id));

    this.productCount -= removedCount;
    this.updateMobileMessage();
    this.updateSessionStorage();

    this.dispatchEvent(
      new CustomEvent('mini-cart:remove', {
        detail: { variantIds },
      })
    );

    this.render();

    activeSwatchTiles.forEach((tile) => {
      const swatchButton = tile.querySelector('swatch-collection-button');
      swatchButton.updateState();

      Shopify.analytics.publish('swatch_removed', {
        title: swatchButton.dataset.title,
        option_name: swatchButton.dataset.title.split(' - ')[0],
        product_id: swatchButton.dataset.productId,
        variant_id: swatchButton.dataset.variantId,
        sku: swatchButton.dataset.sku,
        price: {amount: parseFloat(swatchButton.dataset.price) || 0, currency: Shopify.currency.active},
        image: swatchButton.dataset.image,
        category: swatchButton.dataset.title.split(' - ')[1],
      });
    });
  }

  updateMobileMessage() {
    const mobileMessage = this.querySelector('.mini-cart__mobile-message');
    mobileMessage.textContent = this.productCount > 0 ? `+${this.productCount}` : this.defaultMessage;
  }

  async addToCart() {
    // make an array of products in this format { id: someid, quantity: 1}
    const products = this.products.map((product) => ({ id: product.variant_id, quantity: 1 }));
    await Shopify.addToCart(products);

    this.products = [];
    this.productCount = 0;
    this.updateMobileMessage();
    this.updateSessionStorage();
    this.render();
    this.dispatchEvent(new CustomEvent('mini-cart:atc'));
  }

  updateSessionStorage() {
    sessionStorage.setItem(MiniCart.STORAGE_KEY, JSON.stringify(this.products));
  }

  render() {
    if (this.products.length > 0) {
      const wasExpanded = this.productsGrid.classList.contains('mini-cart__products--expanded');
      
      this.productsGrid.innerHTML = this.products
        .map(
          (product) => `
        <button class="mini-cart__product" data-id="${product.variant_id}" aria-label="Remove {{ product.title }} from Mini Cart">
          <img src="${product.image}" alt="${product.title}" height=50 width=50>
        </button>
      `
        )
        .join('');

      // If it was expanded, add the Less button back
      if (wasExpanded) {
        const lessButton = document.createElement('button');
        lessButton.className = 'btn--underline mini-cart__desktop-toggle mini-cart__less-button';
        lessButton.textContent = this.desktopToggleButton.dataset.closeMessage;
        this.productsGrid.appendChild(lessButton);
        
        // Add click handler to the Less button
        lessButton.addEventListener('click', () => {
          this.productsGrid.classList.remove('mini-cart__products--expanded');
          lessButton.remove();
          this.updateDesktopToggleCartCount();
          this.desktopToggleButton.setAttribute('aria-expanded', 'false');
        });
      }

      if (this.desktopToggleButton.getAttribute('aria-expanded') === 'false') {
        this.updateDesktopToggleCartCount();
      }

      const subtotal = this.products.reduce((acc, product) => acc + product.price, 0);
      const subtotalContainer = this.querySelector('.mini-cart__subtotal');

      // Hide subtotal if it's $0, show it if it's above 0
      if (subtotal > 0) {
        subtotalContainer.style.display = '';
        subtotalContainer.textContent = Shopify.formatMoney(subtotal);
      } else {
        subtotalContainer.style.display = 'none';
      }

      this.querySelectorAll('.mini-cart__product').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const id = e.target.closest('button').dataset.id;
          this.remove(id);
        });
      });
    }

    this.classList.toggle('mini-cart--visible', this.products.length > 0);
  }
}

customElements.define('mini-cart', MiniCart);

class SwatchCollectionButton extends HTMLElement {
  constructor() {
    super();
    this.swatchGrid = this.closest('swatch-grid');
    this.button = this.querySelector('button');

    this.button.addEventListener('click', this.handleClick.bind(this));
    this.updateState();
    this.swatchGrid.miniCart.addEventListener('mini-cart:atc', this.updateState.bind(this));
  }

  handleClick() {
    const isInMiniCart = this.isInMiniCart();
    if (isInMiniCart) {
      this.swatchGrid.removeFromMiniCart([this.dataset.id]);
    } else {
      this.swatchGrid.addToMiniCart([
        {
          variant_id: this.dataset.id,
          title: this.dataset.title,
          image: this.dataset.image,
          price: parseFloat(this.dataset.price) || 0,
        },
      ]);
      Shopify.analytics.publish('swatch_added', {
        title: this.dataset.title,
        option_name: this.dataset.title.split(' - ')[0],
        product_id: this.dataset.productId,
        variant_id: this.dataset.variantId,
        sku: this.dataset.sku,
        price: {amount: parseFloat(this.dataset.price) || 0, currency: Shopify.currency.active},
        image: this.dataset.image,
        category: this.dataset.title.split(' - ')[1],
      });
    }

    this.updateState();
  }

  updateState() {
    const isInMiniCart = this.isInMiniCart();
    this.button.classList.toggle('swatch-group__item-button--in-cart', isInMiniCart);
    // this.button.textContent = isInMiniCart ? 'Remove Swatch' : 'Add Swatch';
  }

  isInMiniCart() {
    return this.swatchGrid.miniCart.products.some((product) => product.variant_id === this.dataset.id);
  }
}

customElements.define('swatch-collection-button', SwatchCollectionButton);
